/**
 * GDPR-style cookie consent context.
 * Manages consent state: essential (always on), analytics, marketing.
 * Persists to first-party cookie (SameSite=Lax, path=/, 1y expiry).
 * Does NOT store personal memory content - only lightweight preferences.
 */
import React, { createContext, useContext, useEffect, useState, useCallback } from 'react'
import { getConsentCookie, setConsentCookie } from '../utils/cookieConsentStorage'

const CONSENT_VERSION = 2

export const CONSENT_CATEGORIES = {
  essential: 'essential',
  analytics: 'analytics',
  marketing: 'marketing',
}

const DEFAULT_CONSENT = {
  version: CONSENT_VERSION,
  essential: true,
  analytics: false,
  marketing: false,
  timestamp: null,
}

const ConsentContext = createContext(null)

export const useConsent = () => {
  const ctx = useContext(ConsentContext)
  if (!ctx) {
    throw new Error('useConsent must be used within ConsentProvider')
  }
  return ctx
}

export const ConsentProvider = ({ children }) => {
  const [consent, setConsentState] = useState(null)
  const [bannerDismissed, setBannerDismissed] = useState(false)
  const [preferencesModalOpen, setPreferencesModalOpen] = useState(false)

  const loadConsent = useCallback(() => {
    try {
      // Read from first-party cookie
      let stored = getConsentCookie()
      // Migrate from localStorage if present (one-time)
      if (!stored && typeof localStorage !== 'undefined') {
        stored = localStorage.getItem('franca_cookie_consent')
        if (stored) {
          setConsentCookie(stored)
          try {
            localStorage.removeItem('franca_cookie_consent')
          } catch (_) {}
        }
      }
      if (stored) {
        const parsed = JSON.parse(stored)
        if (parsed && parsed.version === CONSENT_VERSION) {
          return {
            ...DEFAULT_CONSENT,
            ...parsed,
            essential: true,
          }
        }
      }
    } catch (e) {
      console.warn('Could not parse consent from cookie:', e)
    }
    return null
  }, [])

  const saveConsent = useCallback(
    (newConsent) => {
      const toSave = {
        ...newConsent,
        version: CONSENT_VERSION,
        timestamp: new Date().toISOString(),
      }
      try {
        setConsentCookie(JSON.stringify(toSave))
        setConsentState(toSave)
      } catch (e) {
        console.warn('Could not save consent:', e)
      }
    },
    []
  )

  useEffect(() => {
    const loaded = loadConsent()
    if (loaded) {
      setConsentState(loaded)
      // Only hide banner if user has explicitly consented (valid timestamp)
      setBannerDismissed(loaded.timestamp != null)
    } else {
      setConsentState(DEFAULT_CONSENT)
      setBannerDismissed(false)
    }
  }, [loadConsent])

  const acceptAll = useCallback(() => {
    const newConsent = {
      ...DEFAULT_CONSENT,
      analytics: true,
      marketing: true,
      timestamp: new Date().toISOString(),
    }
    saveConsent(newConsent)
    setBannerDismissed(true)
  }, [saveConsent])

  const rejectNonEssential = useCallback(() => {
    const newConsent = {
      ...DEFAULT_CONSENT,
      analytics: false,
      marketing: false,
      timestamp: new Date().toISOString(),
    }
    saveConsent(newConsent)
    setBannerDismissed(true)
  }, [saveConsent])

  const updatePreferences = useCallback(
    (updates) => {
      const current = consent || DEFAULT_CONSENT
      const newConsent = {
        ...current,
        ...updates,
        essential: true,
        timestamp: new Date().toISOString(),
      }
      saveConsent(newConsent)
    },
    [consent, saveConsent]
  )

  const hasConsented = consent !== null && consent.timestamp != null
  const showBanner = !bannerDismissed && !hasConsented

  const value = {
    consent: consent || DEFAULT_CONSENT,
    hasConsented,
    showBanner,
    acceptAll,
    rejectNonEssential,
    updatePreferences,
    openPreferences: () => setPreferencesModalOpen(true),
    closePreferences: () => setPreferencesModalOpen(false),
    preferencesModalOpen,
    setPreferencesModalOpen,
    dismissBanner: () => setBannerDismissed(true),
    isAnalyticsAllowed: () => (consent || DEFAULT_CONSENT).analytics,
    isMarketingAllowed: () => (consent || DEFAULT_CONSENT).marketing,
  }

  return (
    <ConsentContext.Provider value={value}>
      {children}
    </ConsentContext.Provider>
  )
}
